import "../../../../.cache/static-styles-react/build-javascript/compiled.js";
/** @jsxRuntime automatic @jsxImportSource @terraindigital/static-styles-react */
import { cssRef as _cssRef, mergeComponentProps as _mergeComponentProps, mergeProps as _mergeProps, mergeExternalProps as _mergeExternalProps } from "@terraindigital/static-styles-react";
const _staticStyles = _cssRef("sidebar-mobile-accordion__drawertitle-1ess7s", {
  position: "absolute",
  left: "-10000px",
  width: "1px",
  height: "1px",
  overflow: "hidden"
});
const _staticStyles2 = _cssRef("sidebar-mobile-accordion__drawerdescription-1ess7s", {
  position: "absolute",
  left: "-10000px",
  width: "1px",
  height: "1px",
  overflow: "hidden"
});
import React from 'react';
import { motion } from 'framer-motion';
// eslint-disable-next-line import/no-unresolved
import { useLocation } from '@reach/router';
import useSiteNavigation from '../use-site-navigation';
import type { SiteNavigationPrimary, SiteNavigationPrimaryLevel, SiteNavigationPrimarySublevel, SiteNavigationSecondary } from '../navigation/site-navigation-types';
import { useMicrosite } from '../../../contexts/franchise/microsite-context';
import Link from '../../primitives/link';
import Logo from '../../primitives/logo';
import Container from '../../primitives/grid/container';
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '../../primitives/drawer';
import NavIcon from '../../../images/svg/header-nav.svg';
import SiteSearchBar from '../../site-search/site-search-input';
import Image from '../../primitives/image';
import Caret from '../sidebar-header/caret';
import CloseButton from '../../primitives/close-button';
import MobileCta from './mobile-cta';
import triggerInvoca from '../../../invoca/trigger-invoca';
import MobileLocationDrawer from './mobile-location-drawer';
import { useNavigationContext } from '../../../contexts/navigation-context';
import { MenuImage, MenuLink } from '../types';
import { filterSiteNavigationPrimaryFromField } from '../utils/filter-navigation-items';
import { useNavigationStack, type NavigationStackEntry } from '../../../hooks/use-navigation-stack';
import styles, { MOBILE_ACCORDION_ROW_ICON_SIZES } from './mobile-header.styles';
import { colors } from '../../primitives/colors';
const primaryLevelRowIcon = (row: SiteNavigationPrimaryLevel): MenuImage | null | undefined => row.icon?.url ? {
  title: row.icon.title ?? '',
  url: row.icon.url
} : null;

/** Leaf links from corporate top bar (`secondary`) for mobile accordion behaviour aligned with desktop dropdown. */
const flattenCorporateSectionLinks = (section: SiteNavigationSecondary): MenuLink[] => (section.items ?? []).flatMap(row => row ? (row.items ?? []).reduce<MenuLink[]>((acc, leaf) => {
  if (leaf?.title && leaf?.link) {
    acc.push({
      title: leaf.title,
      href: leaf.link
    });
  }
  return acc;
}, []) : []);
const corporateSectionHasDropdown = (section: SiteNavigationSecondary): boolean => flattenCorporateSectionLinks(section).length > 0;
const SidebarMobileAccordion = (): JSX.Element => {
  const siteNav = useSiteNavigation();
  const menuToUse = filterSiteNavigationPrimaryFromField(siteNav?.primary ?? []);
  const {
    isMicrosite,
    permalink: micrositePermalink
  } = useMicrosite();
  const {
    registerMobileNavClose
  } = useNavigationContext();
  const location = useLocation();
  const drawerRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [locationDrawerOpen, setLocationDrawerOpen] = React.useState(false);
  const [openCorporateTitle, setOpenCorporateTitle] = React.useState<string | null>(null);

  // Use custom navigation stack hook for managing multi-level menu navigation
  const {
    navigationStack,
    navigateForward,
    navigateBackward,
    resetNavigation
  } = useNavigationStack<SiteNavigationPrimary>(menuToUse, 'Main Menu');
  React.useEffect(() => {
    if (open) {
      triggerInvoca();
    }
  }, [open]);

  // Cleanup: Close drawer when component unmounts (e.g., when switching to desktop viewport)
  // This ensures the drawer properly closes when viewport changes from mobile to desktop
  React.useEffect(() => () => {
    // Cleanup function runs on unmount
    // Force close drawer and clean up body scroll lock
    setOpen(false);
    resetNavigation();

    // Remove any lingering Radix UI body attributes that block scroll
    document.body.style.removeProperty('pointer-events');
    document.body.style.removeProperty('overflow');
    document.body.removeAttribute('data-scroll-locked');
  }, [resetNavigation]);

  // Register mobile navigation close function with the context
  const closeMobileNav = React.useCallback((): void => {
    setOpen(false);
    resetNavigation();
  }, [resetNavigation]);
  React.useEffect(() => {
    registerMobileNavClose(closeMobileNav);
  }, [registerMobileNavClose, closeMobileNav]);
  React.useEffect(() => {
    setOpen(false);
    resetNavigation();
    setOpenCorporateTitle(null);
  }, [location.pathname, resetNavigation]);
  const corporateSecondary = siteNav?.secondary ?? null;
  return <>
            <div ref={drawerRef} />
            {drawerRef?.current && open && <Drawer open={open} modal onOpenChange={isOpen => {
      // Handle external close events (like escape key, overlay click)
      if (!isOpen) {
        setOpen(false);
        resetNavigation();
        setOpenCorporateTitle(null);
      }
    }}>

                    <DrawerContent placement="top" size="full" container={drawerRef?.current || undefined} animation="fade">

                        <DrawerTitle {..._mergeComponentProps({
          css: _staticStyles
        })}>
                            Main navigation menu
                        </DrawerTitle>
                        <DrawerDescription {..._mergeComponentProps({
          css: _staticStyles2
        })}>
                            Navigate through site sections and services
                        </DrawerDescription>

                        <div {..._mergeProps({
          id: "mobile-nav-drawer",
          role: "dialog",
          "aria-label": "Main navigation",
          "aria-modal": "true",
          css: styles.drawerContentOuterContainer
        })}>

                            <div {..._mergeProps({
            css: styles.drawerWidthContainer
          })}>
                                <Container>
                                    <div {..._mergeProps({
                css: styles.closeButtonContainer
              })}>
                                        <button type="button" onClick={() => {
                  setOpen(false);
                  resetNavigation();
                  setOpenCorporateTitle(null);
                }} aria-label="Close menu and return to SERVPRO home">
                                            <Logo {..._mergeComponentProps({
                    css: styles.logo,
                    "aria-hidden": "true",
                    focusable: "false"
                  })} />
                                        </button>

                                        <CloseButton {..._mergeComponentProps({
                  css: styles.closeButton,
                  onClick: () => {
                    setOpen(false);
                    resetNavigation();
                    setOpenCorporateTitle(null);
                  },
                  "aria-label": "Close navigation menu"
                })} />
                                    </div>
                                </Container>

                                <Container {..._mergeComponentProps({
              css: styles.directionTransitionContainer
            })}>

                                    <ul {..._mergeProps({
                css: styles.mainMenuList
              })}>
                                        {menuToUse.map(tab => {
                  if (!tab?.title) return null;
                  const title = tab.title ?? '';
                  const href = tab.link ?? undefined;
                  const leftNavItems = tab.items ?? [];
                  const hasValidChildren = leftNavItems.length > 0 && leftNavItems.some((row: SiteNavigationPrimaryLevel | null | undefined) => Boolean(row && (!!row.link || row.items?.some((l: SiteNavigationPrimarySublevel | null | undefined) => !!l?.link))));
                  if (!hasValidChildren) {
                    if (!href) return null;
                    return <li key={`level1-${title}`}>
                                                        <Link {..._mergeComponentProps({
                        key: `main-nav-${href}-${title}`,
                        to: href,
                        css: styles.mainMenuItem
                      })}>
                                                            <span {..._mergeProps({
                          css: styles.mainMenuItemText
                        })}>{title}</span>
                                                        </Link>
                                                    </li>;
                  }
                  const validL2Rows = leftNavItems.filter((row): row is SiteNavigationPrimaryLevel => {
                    if (!row?.title) return false;
                    const hasRowHref = !!row.link;
                    const hasLeaves = row.items?.some(l => Boolean(l?.link && l?.title));
                    return hasRowHref || Boolean(hasLeaves);
                  }).map(row => ({
                    ...row,
                    items: (row.items ?? []).filter((l): l is SiteNavigationPrimarySublevel => Boolean(l?.link && l?.title))
                  }));
                  const isSelected = navigationStack.some((stackItem: NavigationStackEntry) => 'title' in stackItem && stackItem.title === title);
                  return <li key={`level1-${title}`}>
                                                    <button {..._mergeProps({
                      key: `main-nav-button-${title}-${href}`,
                      type: "button",
                      css: styles.mainMenuItem,
                      onClick: () => {
                        setOpenCorporateTitle(null);
                        if (isSelected) {
                          resetNavigation();
                        }
                        if (!isSelected) {
                          if (navigationStack.length > 1) {
                            resetNavigation();
                          }
                          navigateForward(validL2Rows, title ?? '', href || undefined);
                        }
                      },
                      "aria-haspopup": "true",
                      "aria-expanded": "false",
                      "aria-label": `Open ${title} submenu`
                    })}>
                                                        <motion.span {..._mergeExternalProps({
                        css: styles.mainMenuItemText,
                        "aria-hidden": "true",
                        initial: false,
                        animate: {
                          color: isSelected ? colors.primaryOrange : colors.shadesBlack
                        },
                        transition: {
                          duration: 0.2,
                          ease: 'easeOut'
                        }
                      })}>
                                                            {title}
                                                        </motion.span>
                                                        <motion.span {..._mergeExternalProps({
                        css: [styles.arrowContainer, isSelected && styles.activeArrow],
                        "aria-hidden": "true",
                        animate: {
                          rotate: isSelected ? -180 : 0,
                          color: isSelected ? colors.primaryOrange : colors.shadesBlack
                        },
                        transition: {
                          duration: 0.2,
                          ease: 'easeOut'
                        }
                      })}>
                                                            <Caret />
                                                        </motion.span>
                                                    </button>
                                                    <ul>
                                                        {validL2Rows.length > 0 && validL2Rows.map(row => {
                        if (!row?.title) return null;
                        const rowTitle = row.title ?? '';
                        const rowHref = row.link ?? undefined;
                        const leaves = row.items ?? [];
                        const rowIcon = primaryLevelRowIcon(row);
                        const hasValidSubItems = leaves.length > 0;
                        if (hasValidSubItems) {
                          const isSelectedSubmenu = navigationStack.some((stackItem: NavigationStackEntry) => 'title' in stackItem && stackItem.title === rowTitle);
                          return <motion.li initial={{
                            height: 0,
                            opacity: 0
                          }} animate={{
                            height: isSelected ? 'auto' : 0,
                            opacity: 1
                          }} exit={{
                            height: 0,
                            opacity: 0
                          }} transition={{
                            duration: 0.25,
                            ease: 'easeOut'
                          }} style={{
                            overflow: 'hidden',
                            willChange: 'height, opacity'
                          }} key={`level2-${rowTitle}-${rowHref ?? 'nohref'}`}>
                                                                        <button {..._mergeProps({
                              key: `l2-button-${rowTitle}-${rowHref}`,
                              type: "button",
                              css: styles.serviceItemWithIcon,
                              onClick: () => {
                                if (isSelectedSubmenu) {
                                  navigateBackward();
                                }
                                if (!isSelectedSubmenu) {
                                  if (navigationStack.length > 2) {
                                    navigateBackward();
                                  }
                                  const leafLinks = leaves.map(l => ({
                                    title: l?.title ?? '',
                                    href: l?.link ?? ''
                                  })).filter((item: MenuLink): item is MenuLink => !!item.href && !!item.title);
                                  navigateForward(leafLinks, rowTitle || 'Submenu', rowHref || undefined);
                                }
                              },
                              "aria-haspopup": "true",
                              "aria-expanded": "false",
                              "aria-label": `Open ${rowTitle} submenu`
                            })}>
                                                                            <span {..._mergeProps({
                                css: styles.serviceItemLeft
                              })}>
                                                                                {rowIcon ? <Image {..._mergeComponentProps({
                                  css: styles.serviceIcon,
                                  image: rowIcon,
                                  sizes: MOBILE_ACCORDION_ROW_ICON_SIZES,
                                  "aria-hidden": "true"
                                })} /> : <span {..._mergeProps({
                                  css: styles.serviceIcon,
                                  "aria-hidden": "true"
                                })} />}
                                                                                <motion.span {..._mergeExternalProps({
                                  css: styles.subNavMenuItemText,
                                  "aria-hidden": "true",
                                  initial: false,
                                  animate: {
                                    color: isSelectedSubmenu ? colors.primaryOrange : colors.shadesBlack
                                  },
                                  transition: {
                                    duration: 0.2,
                                    ease: 'easeOut'
                                  }
                                })}>
                                                                                    {rowTitle}
                                                                                </motion.span>
                                                                            </span>
                                                                            <motion.span {..._mergeExternalProps({
                                css: [styles.arrowContainer, styles.arrowSubNavItem, isSelectedSubmenu && styles.activeArrow],
                                "aria-hidden": "true",
                                initial: true,
                                animate: {
                                  rotate: isSelectedSubmenu ? -180 : 0,
                                  color: isSelectedSubmenu ? colors.primaryOrange : colors.grey400
                                },
                                transition: {
                                  duration: 0.2,
                                  ease: 'easeOut'
                                }
                              })}>
                                                                                <Caret />
                                                                            </motion.span>
                                                                        </button>
                                                                        <ul>
                                                                            {leaves.map(leaf => {
                                const leafHref = leaf?.link;
                                const leafTitle = leaf?.title;
                                if (!leafHref || !leafTitle) return null;
                                return <motion.li initial={{
                                  height: 0,
                                  opacity: 0
                                }} animate={{
                                  height: isSelectedSubmenu ? 'auto' : 0,
                                  opacity: 1
                                }} exit={{
                                  height: 0,
                                  opacity: 0
                                }} transition={{
                                  duration: 0.25,
                                  ease: 'easeOut'
                                }} style={{
                                  overflow: 'hidden',
                                  willChange: 'height, opacity'
                                }} key={`level3-${leafHref}-${leafTitle}`}>
                                                                                        <Link {..._mergeComponentProps({
                                    to: leafHref,
                                    css: styles.serviceItemWithIcon
                                  })}>
                                                                                            <span {..._mergeProps({
                                      css: [styles.subNavMenuItemText, styles.level2ListItem]
                                    })}>{leafTitle}</span>
                                                                                        </Link>
                                                                                    </motion.li>;
                              })}
                                                                        </ul>
                                                                    </motion.li>;
                        }
                        if (!rowHref) return null;
                        return <motion.li initial={{
                          height: 0,
                          opacity: 0
                        }} animate={{
                          height: isSelected ? 'auto' : 0,
                          opacity: 1
                        }} exit={{
                          height: 0,
                          opacity: 0
                        }} transition={{
                          duration: 0.25,
                          ease: 'easeOut'
                        }} style={{
                          overflow: 'hidden',
                          willChange: 'height, opacity'
                        }} key={`level2-${rowTitle}-${rowHref}`}>
                                                                    <Link {..._mergeComponentProps({
                            to: rowHref,
                            css: styles.serviceItemWithIcon
                          })}>
                                                                        <span {..._mergeProps({
                              css: styles.serviceItemLeft
                            })}>
                                                                            {rowIcon ? <Image {..._mergeComponentProps({
                                css: styles.serviceIcon,
                                image: rowIcon,
                                sizes: MOBILE_ACCORDION_ROW_ICON_SIZES,
                                "aria-hidden": "true"
                              })} /> : <span {..._mergeProps({
                                css: styles.serviceIcon,
                                "aria-hidden": "true"
                              })} />}
                                                                            <span {..._mergeProps({
                                css: styles.subNavMenuItemText
                              })}>{rowTitle}</span>
                                                                        </span>
                                                                    </Link>
                                                                </motion.li>;
                      })}
                                                    </ul>
                                                </li>;
                })}

                                        {corporateSecondary && corporateSecondary.length > 0 && (corporateSecondary ?? []).filter((s): s is SiteNavigationSecondary => Boolean(s)).map(section => {
                  const title = section.title ?? '';
                  const href = section.link ?? '';
                  const leafLinks = flattenCorporateSectionLinks(section);
                  const hasDropdown = corporateSectionHasDropdown(section);
                  if (!title) return null;
                  if (!hasDropdown && !href) return null;
                  if (hasDropdown) {
                    const isCorpOpen = openCorporateTitle === title;
                    return <li key={`level1-corp-${title}`}>
                                                        <button {..._mergeProps({
                        type: "button",
                        css: styles.mainMenuItem,
                        onClick: () => {
                          resetNavigation();
                          setOpenCorporateTitle(isCorpOpen ? null : title);
                        },
                        "aria-expanded": isCorpOpen,
                        "aria-haspopup": "true",
                        "aria-label": `${isCorpOpen ? 'Collapse' : 'Expand'} ${title} menu`
                      })}>
                                                            <motion.span {..._mergeExternalProps({
                          css: styles.mainMenuItemText,
                          "aria-hidden": true,
                          initial: false,
                          animate: {
                            color: isCorpOpen ? colors.primaryOrange : colors.shadesBlack
                          },
                          transition: {
                            duration: 0.2,
                            ease: 'easeOut'
                          }
                        })}>
                                                                {title}
                                                            </motion.span>
                                                            <motion.span {..._mergeExternalProps({
                          css: [styles.arrowContainer, isCorpOpen && styles.activeArrow],
                          "aria-hidden": true,
                          animate: {
                            rotate: isCorpOpen ? -180 : 0,
                            color: isCorpOpen ? colors.primaryOrange : colors.shadesBlack
                          },
                          transition: {
                            duration: 0.2,
                            ease: 'easeOut'
                          }
                        })}>
                                                                <Caret />
                                                            </motion.span>
                                                        </button>
                                                        <ul className="sidebar-mobile-accordion__ul-1n0nku">
                                                            {leafLinks.map(link => <motion.li key={`corp-leaf-${title}-${link.href}`} initial={{
                          height: 0,
                          opacity: 0
                        }} animate={{
                          height: isCorpOpen ? 'auto' : 0,
                          opacity: isCorpOpen ? 1 : 0
                        }} transition={{
                          duration: 0.25,
                          ease: 'easeOut'
                        }} style={{
                          overflow: 'hidden'
                        }}>
                                                                    <Link {..._mergeComponentProps({
                            to: link.href ?? '#',
                            css: styles.serviceItemWithIcon
                          })}>
                                                                        <span {..._mergeProps({
                              css: styles.subNavMenuItemText
                            })}>{link.title}</span>
                                                                    </Link>
                                                                </motion.li>)}
                                                        </ul>
                                                    </li>;
                  }
                  return <li key={`level1-${title}`}>
                                                    <Link {..._mergeComponentProps({
                      key: `main-nav-${href}-${title}`,
                      to: href,
                      css: styles.mainMenuItem
                    })}>
                                                        <span {..._mergeProps({
                        css: styles.mainMenuItemText
                      })}>{title}</span>
                                                    </Link>
                                                </li>;
                })}
                                    </ul>

                                    {!isMicrosite && <div {..._mergeProps({
                css: styles.siteSearchBar
              })}>
                                            <SiteSearchBar />
                                        </div>}
                                </Container>

                            </div>
                            <div {..._mergeProps({
            css: styles.ctaSticky
          })}>
                                <MobileCta inverse setOpen={setLocationDrawerOpen} />
                            </div>
                        </div>

                    </DrawerContent>
                </Drawer>}
            <MobileLocationDrawer open={locationDrawerOpen} setOpen={setLocationDrawerOpen} />
            <Container {..._mergeComponentProps({
      css: styles.headerOuterContainer
    })}>
                <div {..._mergeProps({
        css: styles.headerInnerContainer
      })}>
                    <Link to={isMicrosite && micrositePermalink ? micrositePermalink : '/'} aria-label="SERVPRO Home">
                        <Logo {..._mergeComponentProps({
            css: styles.logo,
            "aria-hidden": "true"
          })} />
                    </Link>
                    <button type="button" onClick={() => {
          setOpen(true);
        }} aria-label="Open navigation menu" aria-expanded={open} aria-controls="mobile-nav-drawer">
                        <NavIcon aria-hidden="true" />
                        <span>Menu</span>
                    </button>
                </div>
            </Container>
            <div {..._mergeProps({
      css: styles.fixedCta
    })}>
                <MobileCta setOpen={setLocationDrawerOpen} />
            </div>
        </>;
};
export default SidebarMobileAccordion;